import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { FeedbackFrontend } from "@performyard/service-contracts";

import { companyFeedbackStreamQuery } from "../../queries/defs/feedback";
import { queryClient } from "../../queryClient";
import {
	CompanyFeedbackCsvQueryParams,
	createFeedback,
	createFeedbackRequest,
	fetchCompanyFeedbackCsv,
	fetchFeedbackByID,
	fetchFeedbackRequestValid,
	fetchHashtags,
	updateFeedback,
	updateFeedbackRequest,
} from "../../services";
import {
	type CompanyFeedbackStreamFilters,
	CompanyFeedbackStreamResult,
} from "../../services/defs/internal-api/feedback";
import { PYUseMutationOptions, PYUseQueryOptions } from "../StandardQueryArguments";
import { useCurrentUser } from "./auth";

export const useSingleFeedback = (feedbackID: string | undefined, disabled = false) => {
	return useQuery({
		queryKey: ["feedback", feedbackID],
		queryFn: () => (feedbackID ? fetchFeedbackByID(feedbackID) : null),
		meta: {
			errorMessage: "There was an error getting the feedback.",
		},
		enabled: !disabled || !!feedbackID,
	});
};

export const useIsFeedbackRequestValid = (requestedTo: string[] = []) => {
	return useQuery({
		queryKey: ["feedback request valid", requestedTo],
		queryFn: () => fetchFeedbackRequestValid(requestedTo),
		meta: {
			errorMessage: "There was an error getting the feedback.",
		},
	});
};

export const useMutateSingleFeedback = (options?: PYUseMutationOptions) => {
	const client = useQueryClient();
	return useMutation({
		mutationKey: ["feedback"],
		mutationFn: (props: {
			id?: string;
			feedbackRequest?: string;
			aboutList: string[];
			content: string;
			feedback_message: string;
			shareWith: string;
		}) => (props?.id ? updateFeedback(props) : createFeedback(props)),
		...options,
		onSuccess: (data, variables, context) => {
			options?.onSuccess && options.onSuccess(data, variables, context);
			client.invalidateQueries({ queryKey: ["search-service", "feedback"] });
			client.invalidateQueries({ queryKey: ["feedback"] });
		},
	});
};

export const useMutateSingleFeedbackRequest = (options?: PYUseMutationOptions) => {
	const client = useQueryClient();
	return useMutation({
		mutationKey: ["feedback-requests"],
		mutationFn: (props: { id?: string; message?: string; requestedTo?: string[] }) =>
			props?.id
				? updateFeedbackRequest({ message: props.message, id: props.id })
				: createFeedbackRequest({ message: props.message, requestedTo: props.requestedTo }),

		...options,
		onSuccess: (data, variables, context) => {
			options?.onSuccess && options.onSuccess(data, variables, context);
			client.invalidateQueries({ queryKey: ["search-service", "feedback_request"] });
		},
	});
};

export const useCompanyFeedbackCsv = (query: Partial<CompanyFeedbackCsvQueryParams>) => {
	return useQuery({
		queryKey: ["company-feedback-csv", query],
		queryFn: () => fetchCompanyFeedbackCsv(query),
		enabled: false,
		meta: {
			errorMessage: "There was an error getting the company feedback CSV.",
		},
	});
};

export const useCompanyHashtags = () => {
	return useQuery<string[]>({
		queryKey: ["company-hashtags"],
		queryFn: () => fetchHashtags(),
		meta: {
			errorMessage: "There was an error getting the company hashtags",
		},
	});
};

export const useFeedbackStream = (
	filters?: CompanyFeedbackStreamFilters,
	onChunk?: (chunk: { data: FeedbackFrontend[] }) => void,
	options?: PYUseQueryOptions<CompanyFeedbackStreamResult>
) => {
	const { data: currentUser } = useCurrentUser();
	const resetQuery = () => {
		// Remove the cached data entirely to force fresh fetch (needed because of infinite staleTime)
		queryClient.removeQueries({ queryKey: companyFeedbackStreamQuery(filters, onChunk).queryKey });
	};
	const query = useQuery<CompanyFeedbackStreamResult>({
		...companyFeedbackStreamQuery(filters, onChunk),
		...options,
		enabled: !!currentUser && options?.enabled !== false,
	});

	return {
		// eslint-disable-next-line @tanstack/query/no-rest-destructuring -- intentional: we want to expose the full query object alongside resetQuery
		...query,
		resetQuery,
	};
};
